import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PublicLanguageSwitcher from '../ui/PublicLanguageSwitcher';
import PublicBackToHomeLink from '../ui/PublicBackToHomeLink';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLegalContent } from '../../content/enrolaLegalContent';

function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      {title ? (
        <h2 className="mb-4 text-lg font-bold text-secondary-900  sm:text-xl">
          {title}
        </h2>
      ) : null}
      {children}
    </section>
  );
}

function LegalPage() {
  const { t, language } = useLanguage();
  const { pathname, search } = useLocation();
  const { effectiveDate, intro, termsSections, privacySections } = useMemo(
    () => getLegalContent(language),
    [language]
  );
  const returnTo = new URLSearchParams(search).get('returnTo');
  const referralReturnPath = returnTo?.startsWith('/referral/') ? returnTo : null;
  const backTo = referralReturnPath || '/';
  const backLabel = referralReturnPath ? t.legal.backToRegistration : t.legal.backToHome;

  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) {
      if (pathname === '/privacy') {
        document.getElementById('privacy-policy')?.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }
    document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
  }, [pathname]);

  return (
    <div className="app-shell min-h-screen bg-secondary-50 ">
      <PublicBackToHomeLink variant="fixed" label={backLabel} to={backTo} />
      <PublicLanguageSwitcher variant="fixed" />

      <div className="mx-auto max-w-3xl px-4 pb-16 pt-24 sm:px-6 sm:pt-28">
        <header className="mb-10 border-b border-secondary-200 pb-8 ">
          <p className="micro text-orange-600 ">
            Enrola
          </p>
          <h1 className="mt-2 text-2xl font-bold text-secondary-900  sm:text-3xl">
            {t.legal.pageTitle}
          </h1>
          <p className="mt-3 text-sm text-secondary-600 ">
            {t.legal.effectiveDate}: {effectiveDate}
          </p>
        </header>

        <div className="space-y-4 text-sm leading-relaxed text-secondary-700  sm:text-base">
          {intro.map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-12 space-y-10">
          <LegalSection id="terms-of-service" title={t.legal.termsHeading}>
            {termsSections.map((section) => (
              <div key={section.id} id={section.id} className="mb-8 scroll-mt-28 last:mb-0">
                <h3 className="mb-3 text-base font-semibold text-secondary-900 ">
                  {section.title}
                </h3>
                <div className="space-y-4">
                  {section.subsections.map((subsection) => (
                    <div key={subsection.title}>
                      <h4 className="mb-1 text-sm font-semibold text-secondary-800 ">
                        {subsection.title}
                      </h4>
                      <p className="text-sm leading-relaxed text-secondary-700 ">
                        {subsection.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </LegalSection>

          <LegalSection id="privacy-policy" title={t.legal.privacyHeading}>
            {privacySections.map((section) => (
              <div key={section.id} id={section.id} className="mb-8 scroll-mt-28 last:mb-0">
                {section.title ? (
                  <h3 className="mb-3 text-base font-semibold text-secondary-900 ">
                    {section.title}
                  </h3>
                ) : null}
                {section.body ? (
                  <p className="whitespace-pre-line text-sm leading-relaxed text-secondary-700 ">
                    {section.body}
                  </p>
                ) : null}
                {section.intro ? (
                  <p className="mb-2 text-sm leading-relaxed text-secondary-700 ">
                    {section.intro}
                  </p>
                ) : null}
                {section.items ? (
                  <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-secondary-700 ">
                    {section.items.map((item) => (
                      <li key={item.slice(0, 48)}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </LegalSection>
        </div>
      </div>
    </div>
  );
}

export default LegalPage;
